// Adding React to allow us to run tests with this component in Platform
// eslint-disable-next-line no-unused-vars
import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useCurrentEditor } from '@tiptap/react';
import { Menu } from '@bevy/bevy-design-system';
import {
  RedoIcon,
  UndoIcon,
  ListBulletIcon,
  ListNumberedIcon,
  TextAlignLeftIcon,
  TextAlignCenterIcon,
  TextAlignRightIcon,
  ImageIcon,
  LinkIcon,
} from '@bevy/bevy-design-system/deprecated';
import ToolbarButton from '../ToolbarButton';
import styles from './styles.module.less';

export const FontStyleSection = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  return (
    <>
      <ToolbarButton
        label='bold'
        name='bold'
        onClick={() => editor.chain().focus().toggleBold().run()}
        isDisabled={!editor.can().chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        label='italic'
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isDisabled={!editor.can().chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        label='underline'
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isDisabled={!editor.can().chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
      >
        <u>U</u>
      </ToolbarButton>
    </>
  );
};

export const HeadingDropdownSection = ({ headings = [1, 2, 3, 4, 5, 6] }) => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  const currentHeading = editor.getAttributes('heading')?.level || '';

  return (
    <div
      className={classNames(styles.headingMenu, {
        [styles.headingMenuActive]: !!currentHeading,
      })}
    >
      <Menu variant='standalone' label={`Heading ${currentHeading}`} size='small' showChevron>
        {headings.map(
          (level) =>
            editor.can().toggleHeading({ level }) && (
              <ToolbarButton
                key={level}
                label={`h${level}`}
                onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                isActive={editor.isActive('heading', { level })}
              >
                H{level}
              </ToolbarButton>
            ),
        )}
      </Menu>
    </div>
  );
};

HeadingDropdownSection.propTypes = {
  headings: PropTypes.arrayOf(PropTypes.number),
};

export const ListItemsSection = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  return (
    <>
      <ToolbarButton
        label='bullet list'
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
      >
        <ListBulletIcon size='small' />
      </ToolbarButton>
      <ToolbarButton
        label='numbered list'
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
      >
        <ListNumberedIcon size='small' />
      </ToolbarButton>
    </>
  );
};

export const HistorySection = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  return (
    <>
      <ToolbarButton
        label='undo'
        onClick={() => editor.chain().focus().undo().run()}
        isDisabled={!editor.can().chain().focus().undo().run()}
      >
        <UndoIcon size='small' />
      </ToolbarButton>
      <ToolbarButton
        label='redo'
        onClick={() => editor.chain().focus().redo().run()}
        isDisabled={!editor.can().chain().focus().redo().run()}
      >
        <RedoIcon size='small' />
      </ToolbarButton>
    </>
  );
};

export const TextAlignSection = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  return (
    <>
      <ToolbarButton
        label='left align'
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive('textAlign', 'left')}
      >
        <TextAlignLeftIcon size='small' />
      </ToolbarButton>
      <ToolbarButton
        label='center align'
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive('textAlign', 'center')}
      >
        <TextAlignCenterIcon size='small' />
      </ToolbarButton>
      <ToolbarButton
        label='right align'
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive('textAlign', 'right')}
      >
        <TextAlignRightIcon size='small' />
      </ToolbarButton>
    </>
  );
};

/**
 * ImageButton requires Editor to have the EditorProps.imageLoader set
 * @returns {React.Component}
 */
export const ImageButton = () => {
  const { editor } = useCurrentEditor();
  const handleSetImage = useCallback(() => {
    const onLoad = (url) => {
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    };
    return editor.options.editorProps.imageLoader(onLoad);
  }, [editor]);

  if (!editor || !editor.options?.editorProps?.imageLoader) return null;

  return (
    <ToolbarButton onClick={handleSetImage} label='image'>
      <ImageIcon size='small' />
    </ToolbarButton>
  );
};
export const LinkButton = () => {
  const { editor } = useCurrentEditor();
  // Check if the editor has a bubble menu for hyperlinks
  const hasBubbleMenu = editor?.options?.bubbleMenuHyperlink;

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    // This is the fallback for when the bubble menu is not available
    // allows users to still add a link
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const openModal = () => {
    // Use bubble menu if it has been registered
    if (hasBubbleMenu) {
      // We're setting the class and default href to an empty string
      // which, in the bubble menu, will trigger the modal to open
      // and initiate a new link editor.
      editor.commands.setLink({ href:  '', class: 'isNewLink' });
      return;
    }

    // Fallback to window prompt
    setLink();
  }

  if (!editor) return null;

  return (
    <ToolbarButton onClick={openModal} label='link'>
      <LinkIcon size='small' />
    </ToolbarButton>
  );
};

// simple component to group attachment buttons
export const AttachmentSection = ({
  children = [<LinkButton key='link' />, <ImageButton key='media' />],
}) => {
  if (!children) return false;
  return children;
};
AttachmentSection.propTypes = {
  children: PropTypes.node,
};

export const ToolBarWrapper = ({ children }) => (
  <div className={styles.toolbarWrapper}>
    <menu
      aria-label='text editor toolbar'
      className={classNames(styles.baseToolbar, 'fixed-toolbar')}
    >
      {children}
    </menu>
  </div>
);

ToolBarWrapper.propTypes = {
  children: PropTypes.node,
};

export const ToolBarSeparator = () => <div className={styles.separatorVertical} />;

/**
 * @name BaseToolbar
 * @description A base toolbar for the rich text editor
 * @param {array} sections - An array of section components to render in the toolbar
 * @see {documentation} https://www.tiptap.dev/api/commands
 * @returns {React.Component}
 */
const BaseToolbar = ({
  sections = [
    HistorySection,
    FontStyleSection,
    AttachmentSection,
    TextAlignSection,
    ListItemsSection,
    HeadingDropdownSection,
  ],
}) => {
  return (
    <ToolBarWrapper>
      {sections.map((Section, index) => {
        return (
          Section && (
            <Fragment key={index}>
              {!!index && <ToolBarSeparator />}
              <Section />
            </Fragment>
          )
        );
      })}
    </ToolBarWrapper>
  );
};

BaseToolbar.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.func),
};

export default BaseToolbar;
